import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Factory, ImageUp, Pencil, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useCategories } from '../lib/hooks';
import { useCatalog } from '../lib/parts';
import type { CatalogManufacturer, CatalogModel } from '../lib/types';
import { ModelImage, prepareModelImage } from './model-image';
import { useToast } from './toast';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Modal,
  PageLoader,
  Select,
  Table,
  Td,
  Textarea,
  Th,
} from './ui';

type ManufacturerDraft = { id?: string; name: string };
type ModelDraft = {
  id?: string;
  manufacturerId: string;
  name: string;
  categoryId: string;
  notes: string;
  /** The picture it has now. */
  imageUrl: string | null;
  /** A new picture, uploaded when the model is saved. */
  picture: File | null;
  removePicture: boolean;
};
type Deleting =
  | { kind: 'manufacturer'; manufacturer: CatalogManufacturer }
  | { kind: 'model'; manufacturer: CatalogManufacturer; model: CatalogModel };

const plural = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`;

/** Settings → Makes & models: the school's manufacturers and models, each model with a default category. */
export function CatalogSettings() {
  const { can } = useAuth();
  const isAdmin = can('ADMIN');
  const toast = useToast();
  const queryClient = useQueryClient();
  const catalog = useCatalog();
  const categories = useCategories();
  const [manufacturerDraft, setManufacturerDraft] = useState<ManufacturerDraft | null>(null);
  const [modelDraft, setModelDraft] = useState<ModelDraft | null>(null);
  const [deleting, setDeleting] = useState<Deleting | null>(null);
  const picture = modelDraft?.picture ?? null;
  const [picturePreview, setPicturePreview] = useState<string | null>(null);

  useEffect(() => {
    if (!picture) {
      setPicturePreview(null);
      return;
    }
    const url = URL.createObjectURL(picture);
    setPicturePreview(url);
    return () => URL.revokeObjectURL(url);
  }, [picture]);

  const refresh = () => {
    // Assets show copies of the names, so renames show up there too.
    for (const key of ['catalog', 'assets', 'asset', 'parts', 'part']) void queryClient.invalidateQueries({ queryKey: [key] });
  };

  const saveManufacturer = useMutation({
    mutationFn: ({ id, name }: ManufacturerDraft) =>
      id ? api.patch(`/manufacturers/${id}`, { name }) : api.post('/manufacturers', { name }),
    onSuccess: () => {
      refresh();
      setManufacturerDraft(null);
      toast.success('Manufacturer saved');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const saveModel = useMutation({
    mutationFn: async ({ id, manufacturerId, imageUrl, picture: file, removePicture, ...fields }: ModelDraft) => {
      // The picture is readied first, so a file that can't be used doesn't leave the model half saved.
      const image = file ? await prepareModelImage(file) : null;
      const saved = id ? await api.patch<CatalogModel>(`/models/${id}`, fields) : await api.post<CatalogModel>('/models', { manufacturerId, ...fields });
      // From here the model exists, so trying again updates it rather than adding it twice.
      if (!id) setModelDraft((draft) => draft && { ...draft, id: saved.id });
      if (image) await api.put(`/models/${saved.id}/image`, { image });
      else if (removePicture && imageUrl) await api.delete(`/models/${saved.id}/image`);
    },
    onSuccess: () => {
      refresh();
      setModelDraft(null);
      toast.success('Model saved');
    },
    onError: (err) => {
      refresh();
      toast.error(errorMessage(err));
    },
  });

  const remove = useMutation({
    mutationFn: (target: Deleting) =>
      target.kind === 'manufacturer' ? api.delete(`/manufacturers/${target.manufacturer.id}`) : api.delete(`/models/${target.model.id}`),
    onSuccess: () => {
      refresh();
      setDeleting(null);
      toast.success('Deleted');
    },
    onError: (err) => {
      setDeleting(null);
      toast.error(errorMessage(err));
    },
  });

  const manufacturers = catalog.data ?? [];
  const newModel = (manufacturerId: string): ModelDraft => ({ manufacturerId, name: '', categoryId: '', notes: '', imageUrl: null, picture: null, removePicture: false });
  const shownPicture = modelDraft?.picture ? picturePreview : modelDraft?.removePicture ? null : (modelDraft?.imageUrl ?? null);

  const submitManufacturer = (event: FormEvent) => {
    event.preventDefault();
    if (manufacturerDraft) saveManufacturer.mutate(manufacturerDraft);
  };
  const submitModel = (event: FormEvent) => {
    event.preventDefault();
    if (modelDraft) saveModel.mutate(modelDraft);
  };

  return (
    <Card>
      <CardHeader
        title="Makes & models"
        description="Picking a model on a new asset preselects its category, and parts are matched to devices by model. A picture helps people recognize their devices."
        actions={
          <>
            <Button size="sm" icon={<Plus />} onClick={() => setManufacturerDraft({ name: '' })}>
              Add manufacturer
            </Button>
            <Button
              size="sm"
              variant="primary"
              icon={<Plus />}
              disabled={manufacturers.length === 0}
              onClick={() => setModelDraft(newModel(manufacturers[0]?.id ?? ''))}
            >
              Add model
            </Button>
          </>
        }
      />

      {catalog.isLoading ? (
        <PageLoader />
      ) : catalog.error ? (
        <ErrorState error={catalog.error} onRetry={() => catalog.refetch()} />
      ) : manufacturers.length === 0 ? (
        <EmptyState
          icon={<Factory />}
          title="No manufacturers yet"
          description="Add the makes and models your school buys. They’re also added automatically from CSV imports and Google devices."
        />
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Manufacturer / model</Th>
              <Th>Default category</Th>
              <Th>Assets</Th>
              <Th>Parts</Th>
              <Th className="text-right">Actions</Th>
            </tr>
          </thead>
          {manufacturers.map((manufacturer) => (
            <tbody key={manufacturer.id} className="divide-y divide-slate-100 border-t border-slate-200">
              <tr className="bg-slate-50/70">
                <Td colSpan={2}>
                  <span className="font-semibold text-slate-900">{manufacturer.name}</span>
                  <span className="ml-2 text-xs text-slate-500">{plural(manufacturer.models.length, 'model')}</span>
                </Td>
                <Td>{manufacturer.assetCount}</Td>
                <Td>
                  <span title="Parts made by this manufacturer">{manufacturer.partCount}</span>
                </Td>
                <Td className="text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" icon={<Plus />} onClick={() => setModelDraft(newModel(manufacturer.id))}>
                    Model
                  </Button>
                  <Button size="sm" variant="ghost" icon={<Pencil />} onClick={() => setManufacturerDraft({ id: manufacturer.id, name: manufacturer.name })}>
                    Rename
                  </Button>
                  {isAdmin && (
                    <Button size="sm" variant="ghost" icon={<Trash2 />} onClick={() => setDeleting({ kind: 'manufacturer', manufacturer })}>
                      Delete
                    </Button>
                  )}
                </Td>
              </tr>
              {manufacturer.models.length === 0 && (
                <tr>
                  <Td colSpan={5} className="pl-9 text-xs text-slate-400">
                    No models yet
                  </Td>
                </tr>
              )}
              {manufacturer.models.map((model) => (
                <tr key={model.id}>
                  <Td className="pl-9">
                    <div className="flex items-center gap-3">
                      <ModelImage url={model.imageUrl} alt="" className="size-10" />
                      <div className="min-w-0">
                        <span className="text-slate-900">{model.name}</span>
                        {model.notes && <p className="text-xs text-slate-500">{model.notes}</p>}
                      </div>
                    </div>
                  </Td>
                  <Td>{model.category ? <Badge>{model.category.name}</Badge> : <span className="text-slate-400">None</span>}</Td>
                  <Td>{model.assetCount}</Td>
                  <Td>
                    {model.partCount > 0 ? (
                      <Link to={`/parts?modelId=${model.id}`} title="Parts that fit this model" className="text-brand-700 hover:underline">
                        {model.partCount}
                      </Link>
                    ) : (
                      0
                    )}
                  </Td>
                  <Td className="text-right whitespace-nowrap">
                    <Button
                      size="sm"
                      variant="ghost"
                      icon={<Pencil />}
                      onClick={() =>
                        setModelDraft({
                          id: model.id,
                          manufacturerId: manufacturer.id,
                          name: model.name,
                          categoryId: model.categoryId ?? '',
                          notes: model.notes ?? '',
                          imageUrl: model.imageUrl,
                          picture: null,
                          removePicture: false,
                        })
                      }
                    >
                      Edit
                    </Button>
                    {isAdmin && (
                      <Button size="sm" variant="ghost" icon={<Trash2 />} onClick={() => setDeleting({ kind: 'model', manufacturer, model })}>
                        Delete
                      </Button>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          ))}
        </Table>
      )}

      <Modal
        open={!!manufacturerDraft}
        onClose={() => setManufacturerDraft(null)}
        size="sm"
        title={manufacturerDraft?.id ? 'Rename manufacturer' : 'Add manufacturer'}
        footer={
          <>
            <Button onClick={() => setManufacturerDraft(null)}>Cancel</Button>
            <Button variant="primary" type="submit" form="manufacturer-form" loading={saveManufacturer.isPending}>
              Save
            </Button>
          </>
        }
      >
        {manufacturerDraft && (
          <form id="manufacturer-form" onSubmit={submitManufacturer}>
            <Field label="Name" required hint={manufacturerDraft.id ? 'Assets with this manufacturer are updated too' : undefined}>
              <Input
                value={manufacturerDraft.name}
                onChange={(e) => setManufacturerDraft({ ...manufacturerDraft, name: e.target.value })}
                autoFocus
                required
                maxLength={120}
                placeholder="e.g. Lenovo"
              />
            </Field>
          </form>
        )}
      </Modal>

      <Modal
        open={!!modelDraft}
        onClose={() => setModelDraft(null)}
        size="sm"
        title={modelDraft?.id ? 'Edit model' : 'Add model'}
        footer={
          <>
            <Button onClick={() => setModelDraft(null)}>Cancel</Button>
            <Button variant="primary" type="submit" form="model-form" loading={saveModel.isPending}>
              Save
            </Button>
          </>
        }
      >
        {modelDraft && (
          <form id="model-form" onSubmit={submitModel} className="space-y-4">
            <Field label="Manufacturer" required>
              <Select
                value={modelDraft.manufacturerId}
                disabled={!!modelDraft.id}
                onChange={(e) => setModelDraft({ ...modelDraft, manufacturerId: e.target.value })}
              >
                {manufacturers.map((manufacturer) => (
                  <option key={manufacturer.id} value={manufacturer.id}>
                    {manufacturer.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Model" required>
              <Input
                value={modelDraft.name}
                onChange={(e) => setModelDraft({ ...modelDraft, name: e.target.value })}
                autoFocus
                required
                maxLength={120}
                placeholder="e.g. 300e Yoga Chromebook Gen 4"
              />
            </Field>
            <Field label="Default category" hint="Preselected when someone adds an asset of this model">
              <Select value={modelDraft.categoryId} onChange={(e) => setModelDraft({ ...modelDraft, categoryId: e.target.value })}>
                <option value="">None</option>
                {categories.data?.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Notes">
              <Textarea
                rows={2}
                maxLength={1000}
                value={modelDraft.notes}
                onChange={(e) => setModelDraft({ ...modelDraft, notes: e.target.value })}
              />
            </Field>
            <Field as="div" label="Picture" hint="Shown on this model’s devices, and to the people who use them. PNG, JPEG, or WebP.">
              <div className="flex items-center gap-4">
                <ModelImage url={shownPicture} alt="" className="size-20" />
                <div className="flex flex-wrap items-center gap-2">
                  <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 focus-within:ring-2 focus-within:ring-brand-500 hover:bg-slate-50">
                    <ImageUp className="size-4" />
                    {shownPicture ? 'Replace picture' : 'Choose picture'}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      className="sr-only"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) setModelDraft({ ...modelDraft, picture: file, removePicture: false });
                        e.target.value = '';
                      }}
                    />
                  </label>
                  {shownPicture && (
                    <Button size="sm" variant="ghost" icon={<Trash2 />} onClick={() => setModelDraft({ ...modelDraft, picture: null, removePicture: true })}>
                      Remove
                    </Button>
                  )}
                </div>
              </div>
            </Field>
          </form>
        )}
      </Modal>

      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting && remove.mutate(deleting)}
        loading={remove.isPending}
        title={
          deleting?.kind === 'manufacturer'
            ? `Delete ${deleting.manufacturer.name}?`
            : deleting?.kind === 'model'
              ? `Delete ${deleting.model.name}?`
              : ''
        }
        description={
          deleting?.kind === 'manufacturer'
            ? deleting.manufacturer.assetCount > 0
              ? `${plural(deleting.manufacturer.assetCount, 'asset')} still use this manufacturer. Change them first.`
              : `Its ${plural(deleting.manufacturer.models.length, 'model')} will be deleted too, and parts will no longer be matched to them.`
            : deleting?.kind === 'model'
              ? deleting.model.assetCount > 0
                ? `${plural(deleting.model.assetCount, 'asset')} still use this model. Change them first.`
                : 'Parts made for this model will no longer be matched to it.'
              : ''
        }
        confirmLabel="Delete"
      />
    </Card>
  );
}
